import { useMemo, useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import HTMLDisplay from "./HTMLDisplay";
import { buildSpacesImageUrl } from "../../utils/spacesImageUrl";

const parseImageList = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

const getLocalizedValue = (item, fieldName, isFrench) => {
  if (!item) return "";
  if (isFrench) {
    const frenchField = `${fieldName}_fr`;
    return item[frenchField] || item[fieldName] || "";
  }
  return item[fieldName] || "";
};

const getLocalizedImages = (item, fieldName, isFrench) => {
  if (!item) return [];
  const baseList = parseImageList(item[fieldName]);
  const frList = parseImageList(item[`${fieldName}_fr`]);
  if (isFrench && frList.length > 0) return frList;
  return baseList;
};

const getImageUrl = (key) => {
  return buildSpacesImageUrl(key, { defaultFolder: "survey-images" });
};

const DescriptionDisplay = ({ item }) => {
  const [lightbox, setLightbox] = useState({ open: false, index: 0 });
  const locale = (process.env.REACT_APP_LOCALE || "en-ca").toLowerCase();
  const isFrench = locale.startsWith("fr");

  const descriptionText = getLocalizedValue(item, "descriptionText", isFrench);
  const images = useMemo(
    () => getLocalizedImages(item, "descriptionImages", isFrench),
    [item, isFrench],
  );

  const hasText = descriptionText && descriptionText.trim() !== "";
  const hasImages = images.length > 0;

  if (!hasText && !hasImages) return null;

  return (
    <Box sx={{ mb: 2, width: "100%", maxWidth: "100%", overflow: "hidden" }}>
      {hasText && (
        <HTMLDisplay
          html={descriptionText}
          noStyle
        />
      )}

      {hasImages && (
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1 }}>
          {images.map((key, index) => (
            <Box
              key={key}
              sx={{
                cursor: "pointer",
                width: 140,
                height: 140,
              }}
              onClick={() => setLightbox({ open: true, index })}
            >
              <img
                src={getImageUrl(key)}
                alt={`Description ${index + 1}`}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            </Box>
          ))}
        </Box>
      )}

      <Dialog
        open={lightbox.open}
        onClose={() => setLightbox({ open: false, index: 0 })}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Typography sx={{ fontSize: "12px" }}>
            Image {lightbox.index + 1} of {images.length}
          </Typography>
          <IconButton
            onClick={() => setLightbox({ open: false, index: 0 })}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 400,
            }}
          >
            <img
              src={getImageUrl(images[lightbox.index])}
              alt={`Full size ${lightbox.index + 1}`}
              style={{
                maxWidth: "100%",
                maxHeight: "80vh",
                objectFit: "contain",
              }}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default DescriptionDisplay;
